/**
 * 「未登録のBB ID」を、もういるスタッフの旧IDとして結び付ける(2026-10-12 発注者指示。D-206)。
 * 昔使っていたID(管理者の MGR01・OSM02 など)が別の人として仮登録されないように、名前で候補を出す。
 * ここは計算だけ(Firebase を触らない)。
 */
import type { BbAccount, Operator } from '@core/lib/types';
import { baseName, nameKey } from './bbReissue';
import { guessFromBbId, type UnknownBbId } from './unknownBbRepo';

/** CSV に出ていた氏名から、比べるための名前を作る(末尾の W・かっこ書き「（RS）」を外す) */
export function unknownNameKey(name: string): string {
  return nameKey(baseName(name.replace(/[（(][^）)]*[）)]\s*$/, '')));
}

/** 名前が同じスタッフ(まとめ済みのマスタは除く。在籍を先に) */
export function linkCandidates(u: Pick<UnknownBbId, 'name'>, operators: Operator[]): Operator[] {
  const key = unknownNameKey(u.name);
  if (!key) return [];
  return operators
    .filter((o) => !o.merged_into && unknownNameKey(o.name) === key)
    .sort((a, b) => Number(b.status === 'active') - Number(a.status === 'active') || a.op_code.localeCompare(b.op_code));
}

/**
 * 結び付ける旧IDの BB ID 欄。無効(旧ID)で足す。
 * 時給(単価)・改定履歴・架電会社は、その人の同じ種類のID(架電内容と出勤形態が同じ → 架電内容が同じ → 先頭)から写す。
 * 写せるIDが無ければ時給は未設定(仮登録と同じ)。
 */
export function oldAccountFor(u: Pick<UnknownBbId, 'bb_id' | 'name'>, operator: Operator): BbAccount {
  const g = guessFromBbId(u.bb_id, u.name);
  const accounts = operator.bb_accounts;
  const src =
    accounts.find((a) => a.business === g.business && a.work_style === g.work_style && a.pay_type === g.pay_type) ??
    accounts.find((a) => a.business === g.business) ??
    accounts[0];
  return {
    work_style: g.work_style,
    pay_type: g.pay_type,
    bb_id: u.bb_id.trim().toUpperCase(),
    ext: null,
    bb_internal_id: null,
    company_id: src?.company_id ?? null,
    business: g.business,
    active: false,
    current_wage: src?.current_wage ?? 0,
    ...(src && !src.wage_pending ? {} : { wage_pending: true }),
    wage_history: src?.wage_history ?? [],
    // どのIDの代わりだったかは分からないので replaced_by は付けない(今のIDが分かるのは付け直し(CSV)のときだけ)
  };
}

export type BulkLinkRow = {
  unknown: UnknownBbId;
  candidates: Operator[];
  /** 最初に選んでおく人。候補が1人(または在籍が1人)のときだけ。2人以上で決められなければ null(画面で選ぶ) */
  suggested: Operator | null;
};

/** まとめて結び付ける(D-207)の候補。名前が同じスタッフがいる行だけ */
export function bulkLinkRows(unknowns: UnknownBbId[], operators: Operator[]): BulkLinkRow[] {
  return unknowns
    .map((u) => {
      const candidates = linkCandidates(u, operators);
      const active = candidates.filter((o) => o.status === 'active');
      const suggested = candidates.length === 1 ? candidates[0]! : active.length === 1 ? active[0]! : null;
      return { unknown: u, candidates, suggested };
    })
    .filter((r) => r.candidates.length > 0);
}

export type BulkRegisterPlan = {
  /** 名前が同じスタッフ(1人に決まる)の旧IDとして結び付ける */
  link: { unknown: UnknownBbId; operator: Operator }[];
  /** 新しい人として仮登録する */
  provisional: UnknownBbId[];
  /** 自動では決められないので登録しない(1件ずつ選ぶ)。名前が同じスタッフが2人以上・選んだ中に同じ名前が2件以上 */
  skipped: UnknownBbId[];
};

/**
 * 選んだ未登録のBB IDを「まとめて登録する」ときの振り分け(2026-10-17 発注者指示)。
 * 1件ずつの「登録する」と同じく、名前が同じスタッフがいれば結び付け(同じ人が2人にならないように)、いなければ仮登録。
 * 選んだ中に同じ名前の新しい人が2件以上あるときは、最初の1件だけ仮登録して残りは飛ばす(仮登録のあとで「結び付ける」が出る)
 */
export function planBulkRegister(unknowns: UnknownBbId[], operators: Operator[]): BulkRegisterPlan {
  const plan: BulkRegisterPlan = { link: [], provisional: [], skipped: [] };
  const newNames = new Set<string>();
  for (const u of unknowns) {
    const candidates = linkCandidates(u, operators);
    if (candidates.length > 0) {
      const active = candidates.filter((o) => o.status === 'active');
      const one = candidates.length === 1 ? candidates[0]! : active.length === 1 ? active[0]! : null;
      if (one) plan.link.push({ unknown: u, operator: one });
      else plan.skipped.push(u);
      continue;
    }
    const key = unknownNameKey(u.name);
    if (key && newNames.has(key)) {
      plan.skipped.push(u);
      continue;
    }
    if (key) newNames.add(key);
    plan.provisional.push(u);
  }
  return plan;
}
